import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import type { Pagination, SingleResponse } from "./types";

export interface Notification {
  id: string;
  tenantId: string;
  userId: string;
  event: string;
  message: string;
  entityId?: string;
  read: boolean;
  timestamp: string;
}

export interface NotificationsListResponse {
  data: Notification[];
  pagination: Pagination;
  unread: number;
}

export interface ChannelPrefs {
  inApp: boolean;
  email: boolean;
}

export interface NotificationPreferences {
  tenantId?: string;
  userId?: string;
  events: Record<string, ChannelPrefs>;
  updatedAt?: string;
}

export interface ListNotificationsParams {
  limit?: number;
  cursor?: string;
}

export function useNotifications(params: ListNotificationsParams = {}) {
  const qs = new URLSearchParams();
  qs.set("limit", String(params.limit ?? 25));
  if (params.cursor) qs.set("cursor", params.cursor);

  return useQuery({
    queryKey: ["notifications", params],
    queryFn: () =>
      apiFetch<NotificationsListResponse>(`/api/v1/notifications?${qs.toString()}`),
    placeholderData: keepPreviousData,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/notifications/${id}/read`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: ["notification-preferences"],
    queryFn: () =>
      apiFetch<SingleResponse<NotificationPreferences>>("/api/v1/notifications/preferences"),
  });
}

export function useUpdateNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (events: Record<string, ChannelPrefs>) =>
      apiFetch<SingleResponse<NotificationPreferences>>("/api/v1/notifications/preferences", {
        method: "PUT",
        body: JSON.stringify({ events }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notification-preferences"] }),
  });
}
